import { useEffect, useMemo, useState, type JSX } from 'react';
import { usePublicPropertyList } from '@/hooks/usePublicProperties';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import { buildContactInfo } from '@/lib/constants/contactInfo';
import { Seo } from '@/components/seo';
import { mergeContentWithDefaults } from '@/lib/utils/contentDefaults';
import { PublicPageLayout } from '@/layouts/PublicPageLayout';
import { DEFAULT_CONTACT_FORM_CONTENT } from '@/components/public/ContactLeadForm';
import { useAvailabilitiesViewMode } from '@/routes/publicProperties/useAvailabilitiesViewMode';
import { DEFAULT_AVAILABILITIES_CONTENT } from '@/routes/publicProperties/availabilitiesContent';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getRecentUnitViews } from '@/lib/utils/recentUnits';
import { DEFAULT_HOME_CONTENT } from '@/routes/PlaceholderHomeContent';
import { AvailabilitySection } from '@/routes/PlaceholderHomeAvailabilitySection';
import { ContactSectionBlock } from '@/routes/PlaceholderHomeContactSection';
import { HomeHeroSection } from '@/routes/PlaceholderHomeHeroSection';
import {
  RecentUnitsSection,
  type RecentUnitEntry,
} from '@/routes/PlaceholderHomeRecentUnitsSection';

export function PlaceholderHome(): JSX.Element {
  const { data, isLoading, isFetching, error } = usePublicPropertyList({ pageSize: 60 });
  const siteSettingsQuery = useSiteSettingsDetail();
  const siteSettings = siteSettingsQuery.data;
  const showLoading = isLoading || isFetching;
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [prefersDataSaver, setPrefersDataSaver] = useState(false);
  const [viewMode, setViewMode] = useAvailabilitiesViewMode();
  const flagshipProperty = data?.items.find(
    (property) => property.propertyCode === '501_ALLIANCE_AVE',
  );
  const heroPosterUrl =
    flagshipProperty?.coverImageUrl ??
    flagshipProperty?.heroMediaUrls.find((url) => url.trim().length > 0) ??
    '';

  const homeContent = siteSettings?.home ?? DEFAULT_HOME_CONTENT;
  const preferText = (value: string, fallback: string) =>
    value.trim().length > 0 ? value : fallback;
  const heroContent = {
    eyebrow: preferText(homeContent.hero.eyebrow, DEFAULT_HOME_CONTENT.hero.eyebrow),
    title: preferText(homeContent.hero.title, DEFAULT_HOME_CONTENT.hero.title),
    description: preferText(homeContent.hero.description, DEFAULT_HOME_CONTENT.hero.description),
  };
  const heroMediaContent = {
    label: preferText(homeContent.heroMedia.label, DEFAULT_HOME_CONTENT.heroMedia.label),
    location: preferText(homeContent.heroMedia.location, DEFAULT_HOME_CONTENT.heroMedia.location),
    ariaLabel: preferText(
      homeContent.heroMedia.ariaLabel,
      DEFAULT_HOME_CONTENT.heroMedia.ariaLabel,
    ),
    url: preferText(homeContent.heroMedia.url, ''),
    mediaType: homeContent.heroMedia.mediaType === 'image' ? 'image' : 'video',
  };
  const heroMediaUrl = heroMediaContent.url.trim();
  const hasHeroMediaUrl = heroMediaUrl.length > 0;
  const isHeroMediaVideo = heroMediaContent.mediaType !== 'image';
  const shouldPlayHeroVideo =
    hasHeroMediaUrl && isHeroMediaVideo && !prefersReducedMotion && !prefersDataSaver;
  const canShowPoster = heroPosterUrl.length > 0;
  const availabilityContent = {
    eyebrow: preferText(
      homeContent.availability.eyebrow,
      DEFAULT_HOME_CONTENT.availability.eyebrow,
    ),
    title: preferText(homeContent.availability.title, DEFAULT_HOME_CONTENT.availability.title),
    description: preferText(
      homeContent.availability.description,
      DEFAULT_HOME_CONTENT.availability.description,
    ),
    ctaLabel: preferText(
      homeContent.availability.ctaLabel,
      DEFAULT_HOME_CONTENT.availability.ctaLabel,
    ),
    ctaHref: preferText(
      homeContent.availability.ctaHref,
      DEFAULT_HOME_CONTENT.availability.ctaHref,
    ),
  };
  const availabilityMessages = {
    emptyMessage: preferText(
      homeContent.availabilityMessages.emptyMessage,
      DEFAULT_HOME_CONTENT.availabilityMessages.emptyMessage,
    ),
    errorMessage: preferText(
      homeContent.availabilityMessages.errorMessage,
      DEFAULT_HOME_CONTENT.availabilityMessages.errorMessage,
    ),
  };
  const contactContent = {
    heading: preferText(homeContent.contact.heading, DEFAULT_HOME_CONTENT.contact.heading),
    description: preferText(
      homeContent.contact.description,
      DEFAULT_HOME_CONTENT.contact.description,
    ),
  };
  const seoContent = {
    title: preferText(homeContent.seo.title, DEFAULT_HOME_CONTENT.seo.title),
    description: preferText(homeContent.seo.description, DEFAULT_HOME_CONTENT.seo.description),
  };
  const contactInfo = buildContactInfo(siteSettings);
  const contactPageFormContent = mergeContentWithDefaults(
    siteSettings?.publicContent.contactPage.form,
    DEFAULT_CONTACT_FORM_CONTENT,
  );
  const availabilitiesContent = mergeContentWithDefaults(
    siteSettings?.publicContent.availabilitiesPage,
    DEFAULT_AVAILABILITIES_CONTENT,
  );
  const availabilityLabels = {
    immediateLabel:
      siteSettings?.publicContent.propertyDetail.labels.unitAvailabilityImmediateLabel ??
      'Immediate',
    negotiableLabel:
      siteSettings?.publicContent.propertyDetail.labels.unitAvailabilityNegotiableLabel ??
      'Negotiable',
  };
  const sharedContactLabels = siteSettings?.publicContent.shared.contactSection;
  const emptyAvailabilityFallback = (
    <p className="rounded-2xl border border-dashed border-surface-subtle bg-white p-6 text-center text-sm text-text-primary/70">
      {availabilityMessages.emptyMessage}
    </p>
  );
  const recentViews = useMemo(() => getRecentUnitViews(), []);
  const recentUnits = useMemo(() => {
    if (!data?.items) {
      return [];
    }
    const propertyMap = new Map(
      data.items.map((property) => [property.propertyCode.toUpperCase(), property]),
    );
    const unique = new Map<string, RecentUnitEntry>();
    recentViews.forEach((view) => {
      const property = propertyMap.get(view.propertyCode.toUpperCase());
      if (!property) {
        return;
      }
      const unit = property.publishedUnits.find((entry) => entry.id === view.unitId);
      if (!unit) {
        return;
      }
      unique.set(`${property.id}-${unit.id}`, { property, unit });
    });
    return Array.from(unique.values()).slice(0, 3);
  }, [data?.items, recentViews]);

  useEffect(() => {
    if (typeof navigator === 'undefined') {
      return;
    }
    const connection = (
      navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string };
      }
    ).connection;
    const saveData = connection?.saveData === true;
    const effectiveType = connection?.effectiveType ?? '';
    const slowNetwork = effectiveType.includes('2g') || effectiveType.includes('slow-2g');
    setPrefersDataSaver(saveData || slowNetwork);
  }, []);

  return (
    <PublicPageLayout>
      <Seo title={seoContent.title} description={seoContent.description} canonicalPath="/" />
      <HomeHeroSection
        heroContent={heroContent}
        heroMediaContent={heroMediaContent}
        heroPosterUrl={heroPosterUrl}
        heroMediaUrl={heroMediaUrl}
        shouldPlayHeroVideo={shouldPlayHeroVideo}
        canShowPoster={canShowPoster}
      />
      <RecentUnitsSection recentUnits={recentUnits} />
      <AvailabilitySection
        content={availabilityContent}
        availabilitiesContent={availabilitiesContent}
        availabilityLabels={availabilityLabels}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        data={data}
        isLoading={showLoading}
        error={error}
        emptyFallback={emptyAvailabilityFallback}
        errorMessage={availabilityMessages.errorMessage}
      />
      <ContactSectionBlock
        contactContent={contactContent}
        contactInfo={contactInfo}
        contactPageFormContent={contactPageFormContent}
        sharedContactLabels={sharedContactLabels}
      />
    </PublicPageLayout>
  );
}
